/**
 * TanStack Query Hooks
 *
 * React Query hooks for data fetching with caching, refetching, and mutations.
 */

import { useQuery, useInfiniteQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import {
  mediaPrefetchCoordinator,
  tier1UrlsForAsset,
  tier2UrlsForAsset,
  resolvePrefetchUrl,
  PREFETCH_PRIORITY,
  ESTIMATE_BYTES,
} from '@/lib/media-prefetch';
import {
  api,
  type Dashboard,
  type UserProfile,
  type MyOrgLocation,
  type PlannerTask,
  type Contact,
  type Deal,
  type Project,
  type Asset,
  type Invoice,
  type AlfieThread,
  type AlfieInsight,
  type GuidanceCall,
  type DiagnosticResult,
  type Activity,
  type ActionPlanSession,
  type UserSettings,
  type UserLearningProgress,
  type MessageAttachment,
  type LibraryAssetFilters,
  // LMS types
  type PathEnrollment,
  type AssessmentAttempt,
  // Gamification types
  type BadgeWithEarnedStatus,
  type LeaderboardEntry,
  type ChallengeWithStatus,
  type PointTransaction,
  // Media types
  type MediaAsset,
  type MediaAssetPurpose,
  type MediaAssetStatus,
  type MediaAssetType,
  // Voice types (from Drizzle schema)
  type UserVoiceSettings,
  type NewUserVoiceSettings,
  // Notification types
  type Notification,
} from './client';

// ===========================================
// Query Keys
// ===========================================
export const queryKeys = {
  // User
  currentUser: ['currentUser'] as const,
  myOrgLocation: ['myOrgLocation'] as const,
  userSettings: ['userSettings'] as const,
  myRoles: ['myRoles'] as const,
  voiceSettings: (userId?: string) => ['voiceSettings', userId] as const,

  // Dashboards
  dashboards: ['dashboards'] as const,
  dashboard: (id: string) => ['dashboard', id] as const,

  // Assessments & Diagnostics
  primeScore: ['primeScore'] as const,
  primeScoreHistory: ['primeScoreHistory'] as const,
  diagnostics: (type?: string) => ['diagnostics', type] as const,
  latestDiagnostic: (type: string) => ['latestDiagnostic', type] as const,

  // Tasks
  todayTasks: ['todayTasks'] as const,
  allTasks: (status?: string) => ['allTasks', status] as const,

  // Notifications
  notifications: ['notifications'] as const,
  unreadNotificationCount: ['unreadNotificationCount'] as const,
  unreadFeedCount: ['unreadFeedCount'] as const,

  // CRM
  contacts: (filters?: Record<string, unknown>) => ['contacts', filters] as const,
  contact: (id: string) => ['contact', id] as const,
  deals: (filters?: Record<string, unknown>) => ['deals', filters] as const,
  deal: (id: string) => ['deal', id] as const,
  projects: (filters?: Record<string, unknown>) => ['projects', filters] as const,
  project: (id: string) => ['project', id] as const,

  // Financial
  financialMetrics: ['financialMetrics'] as const,
  latestFinancialMetrics: ['latestFinancialMetrics'] as const,
  assets: (type?: string) => ['assets', type] as const,
  invoices: (filters?: Record<string, unknown>) => ['invoices', filters] as const,

  // Library Assets (ClickUp-synced business assets)
  libraryAssets: (filters?: LibraryAssetFilters) => ['libraryAssets', filters] as const,

  // Access Matrix
  accessMatrix: ['accessMatrix'] as const,

  // Alfie (AI)
  alfieThreads: ['alfieThreads'] as const,
  alfieMessages: (threadId: string) => ['alfieMessages', threadId] as const,
  alfieInsights: ['alfieInsights'] as const,

  // Learning (LMS)
  learningPaths: (category?: string) => ['learningPaths', category] as const,
  learningPath: (id: string) => ['learningPath', id] as const,
  courses: (category?: string) => ['courses', category] as const,
  course: (id: string) => ['course', id] as const,
  lesson: (id: string) => ['lesson', id] as const,
  myEnrollments: ['myEnrollments'] as const,
  userLearningProgress: (pathId?: string) => ['userLearningProgress', pathId] as const,
  assessment: (id: string) => ['assessment', id] as const,
  moduleAssessments: (moduleId: string) => ['moduleAssessments', moduleId] as const,
  myAssessmentAttempts: (assessmentId?: string) => ['myAssessmentAttempts', assessmentId] as const,
  bestAssessmentScore: (courseId: string) => ['bestAssessmentScore', courseId] as const,
  myCertificates: ['myCertificates'] as const,

  // Gamification (Badges, Points, Leaderboard, Challenges)
  allBadges: ['allBadges'] as const,
  myBadges: ['myBadges'] as const,
  badgesWithEarnedStatus: ['badgesWithEarnedStatus'] as const,
  myPoints: ['myPoints'] as const,
  leaderboard: (timeframe: string) => ['leaderboard', timeframe] as const,
  challenges: ['challenges'] as const,
  myChallenges: ['myChallenges'] as const,

  // Media Assets
  mediaAsset: (id: string) => ['mediaAsset', id] as const,
  mediaAssets: (filters?: Record<string, unknown>) => ['mediaAssets', filters] as const,

  // Coaching
  coaches: (specialty?: string) => ['coaches', specialty] as const,
  coach: (id: string) => ['coach', id] as const,
  guidanceCalls: (filters?: Record<string, unknown>) => ['guidanceCalls', filters] as const,
  upcomingCalls: ['upcomingCalls'] as const,

  // Activity
  recentActivity: (options?: Record<string, unknown>) => ['recentActivity', options] as const,
  actionPlanSessions: (filters?: Record<string, unknown>) => ['actionPlanSessions', filters] as const,
  upcomingSessions: ['upcomingSessions'] as const,
};

// ===========================================
// User Hooks
// ===========================================

export function useCurrentUser() {
  return useQuery({
    queryKey: queryKeys.currentUser,
    queryFn: () => api.getCurrentUser(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
}

/**
 * Record the finance skill level, FIRST ANSWER WINS.
 *
 * A hook in this module and not a `useQueryClient` call inside the dialog, because every test of
 * that dialog mocks `@/api/hooks` — reaching past it for a raw query client broke all 21 of them
 * at once. The component should see one seam, and this is it.
 */
export function useSetFinanceSkillLevel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (level: number) => api.setFinanceSkillLevelIfUnset(level),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates: Partial<UserProfile>) => api.updateUserProfile(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
      // Cross-client propagation is handled server-side: a DB trigger on
      // user_profiles broadcasts profile.updated (see useCommunityProfilesChannel).
    },
  });
}

/** Upload a new avatar, point the profile + auth metadata at it, refresh caches. */
export function useUpdateAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => api.uploadAvatar(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
      // The directory map pin carries the member's avatar.
      queryClient.invalidateQueries({ queryKey: ['community', 'directoryMapPins'] });
    },
  });
}

/** Re-select an existing image URL (the OAuth/sign-in photo) as the avatar. */
export function useSelectAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (url: string) => api.setAvatarUrl(url),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
      // The directory map pin carries the member's avatar.
      queryClient.invalidateQueries({ queryKey: ['community', 'directoryMapPins'] });
    },
  });
}

/** Location on the member's owned org — prefills the profile location form. */
export function useMyOrgLocation() {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.myOrgLocation,
    queryFn: () => api.getMyOrgLocation(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
}

/** Update the org location (re-geocodes the pin); refreshes location + directory. */
export function useUpdateMyOrgLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: Parameters<typeof api.updateMyOrgLocation>[0]): Promise<MyOrgLocation> =>
      api.updateMyOrgLocation(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myOrgLocation });
      queryClient.invalidateQueries({ queryKey: ['community', 'directoryMapPins'] });
    },
  });
}

export function useUserSettings() {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.userSettings,
    queryFn: () => api.getUserSettings(),
    enabled: !!user,
  });
}

export function useUpdateUserSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates: Partial<UserSettings>) => api.updateUserSettings(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userSettings });
    },
  });
}

export function useMyRoles() {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.myRoles,
    queryFn: () => api.getMyRoles(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Helper hook to check if user can author content
export function useCanAuthorContent() {
  const { data: roles = [], isLoading } = useMyRoles();

  const canAuthor = roles.some(
    (assignment) =>
      assignment.role?.code === 'content_admin' ||
      assignment.role?.code === 'external_coach' ||
      assignment.role?.code === 'super_admin'
  );

  return { canAuthor, isLoading };
}

// Helper hook to check if current user holds the super_admin global role.
export function useIsSuperAdmin() {
  const { data: roles = [], isLoading } = useMyRoles();

  const isSuperAdmin = roles.some(
    (assignment) => assignment.role?.code === 'super_admin'
  );

  return { isSuperAdmin, isLoading };
}

// Helper hook to check if current user holds the support_admin role — the
// support/finance staff gate for the billing admin pages (/admin/revenue +
// /admin/subscriptions). Mirrors the admin-billing edge fn's has_permission
// (billing/admin) check.
export function useIsSupportAdmin() {
  const { data: roles = [], isLoading } = useMyRoles();

  const isSupportAdmin = roles.some(
    (assignment) => assignment.role?.code === 'support_admin'
  );

  return { isSupportAdmin, isLoading };
}

// Helper hook: is the current user a salesperson? The SAME check the sales page and the
// hermes `sales-desk` function make (has_permission sales_deals:write), not a role-code
// match, so the page and the server always agree. A failed call counts as "no".
export function useIsSalesperson() {
  const { data: user } = useCurrentUser();
  const { data, isLoading } = useQuery({
    queryKey: ['is-salesperson', user?.id],
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data: allowed, error } = await supabase.rpc('has_permission', {
        p_user_id: user!.id,
        p_resource: 'sales_deals',
        p_action: 'write',
      });
      if (error) {
        console.error('[is-salesperson] has_permission failed', error);
        return false;
      }
      return allowed === true;
    },
  });
  return { isSalesperson: data === true, isLoading };
}

/**
 * Content-entitlement gate — `true` when the user is a paying member (any active
 * community-tier membership OR any active user/org add-on grant), `false` for
 * free-preview users. Mirrors the server pre-gate `user_has_content_entitlement`
 * (migration 335) that fronts the `courses_select` RLS.
 *
 * This is the "are you a member?" axis (Gate 1), deliberately decoupled from any
 * specific content code. Drip pacing rides on THIS, not on `basecamp` access — so
 * every member (WunderPack, Vault, and any FUTURE K&C tier that doesn't carry the
 * `basecamp` code) is exempt from the Basecamp-series drip; only free-preview
 * users get paced.
 */
export function useHasContentEntitlement() {
  const { data: user } = useCurrentUser();
  const userId = user?.id;

  return useQuery({
    queryKey: ['content-entitlement', userId],
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase.rpc('user_has_content_entitlement', {
        p_user_id: userId,
      });
      if (error) throw new Error(error.message);
      return data === true;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes — entitlement rarely changes mid-session
  });
}

// ===========================================
// Dashboard Hooks
// ===========================================

export function useDashboards() {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.dashboards,
    queryFn: () => api.getDashboards(),
    enabled: !!user,
    initialData: [],
  });
}

export function useCreateDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, isDefault }: { name: string; isDefault?: boolean }) =>
      api.createDashboard(name, isDefault),
    onSuccess: (newDashboard) => {
      queryClient.setQueryData<Dashboard[]>(queryKeys.dashboards, (old = []) => [
        newDashboard,
        ...old,
      ]);
    },
  });
}

export function useUpdateDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Dashboard> }) =>
      api.updateDashboard(id, updates),
    onSuccess: (updatedDashboard) => {
      queryClient.setQueryData<Dashboard[]>(queryKeys.dashboards, (old = []) =>
        old.map((d) => (d.id === updatedDashboard.id ? updatedDashboard : d))
      );
    },
  });
}

export function useDeleteDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteDashboard(id),
    onSuccess: (_, deletedId) => {
      queryClient.setQueryData<Dashboard[]>(queryKeys.dashboards, (old = []) =>
        old.filter((d) => d.id !== deletedId)
      );
    },
  });
}

export function useSetDefaultDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.setDefaultDashboard(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards });
    },
  });
}

// ===========================================
// Prime Score & Diagnostic Hooks
// ===========================================

export function usePrimeScore() {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.primeScore,
    queryFn: () => api.getLatestPrimeScore(),
    enabled: !!user,
    retry: false,
  });
}

export function usePrimeScoreHistory(limit = 12) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.primeScoreHistory,
    queryFn: () => api.getPrimeScoreHistory(limit),
    enabled: !!user,
    initialData: [],
  });
}

export function useDiagnosticResults(diagnosticType?: string) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.diagnostics(diagnosticType),
    queryFn: () => api.getDiagnosticResults(diagnosticType),
    enabled: !!user,
    initialData: [],
  });
}

export function useLatestDiagnostic(diagnosticType: string) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.latestDiagnostic(diagnosticType),
    queryFn: () => api.getLatestDiagnostic(diagnosticType),
    enabled: !!user,
  });
}

export function useSaveDiagnosticResult() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (result: Omit<DiagnosticResult, 'id' | 'user_id' | 'created_at'>) =>
      api.saveDiagnosticResult(result),
    onSuccess: (newResult) => {
      queryClient.invalidateQueries({ queryKey: ['diagnostics'] });
      queryClient.setQueryData(queryKeys.latestDiagnostic(newResult.diagnostic_type), newResult);
    },
  });
}

// ===========================================
// Task Hooks
// ===========================================

export function useTodayTasks(limit = 10) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.todayTasks,
    queryFn: () => api.getTodayTasks(limit),
    enabled: !!user,
    initialData: [],
  });
}

export function useAllTasks(status?: PlannerTask['status']) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.allTasks(status),
    queryFn: () => api.getAllTasks(status),
    enabled: !!user,
    initialData: [],
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (task: Omit<PlannerTask, 'id' | 'user_id' | 'created_at' | 'updated_at'>) =>
      api.createTask(task),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.todayTasks });
      queryClient.invalidateQueries({ queryKey: ['allTasks'] });
      if (data?.id) {
        api.logActivity({
          type: 'productivity',
          action: 'created',
          entity_type: 'task',
          entity_id: data.id,
          entity_name: data.title ?? 'Task',
          description: `Task created: ${data.title ?? ''}`,
          metadata: {},
        }).catch(() => {});
      }
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<PlannerTask> }) =>
      api.updateTask(id, updates),
    onSuccess: (updatedTask) => {
      queryClient.setQueryData<PlannerTask[]>(queryKeys.todayTasks, (old = []) =>
        old.map((t) => (t.id === updatedTask.id ? updatedTask : t))
      );
      queryClient.invalidateQueries({ queryKey: ['allTasks'] });
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteTask(id),
    onSuccess: (_, deletedId) => {
      queryClient.setQueryData<PlannerTask[]>(queryKeys.todayTasks, (old = []) =>
        old.filter((t) => t.id !== deletedId)
      );
      queryClient.invalidateQueries({ queryKey: ['allTasks'] });
    },
  });
}

// ===========================================
// Notification Hooks
// ===========================================

export function useNotifications(filter: 'all' | 'unread' | 'mentions' | 'actions' = 'all') {
  const { data: user } = useCurrentUser();

  return useInfiniteQuery({
    queryKey: [...queryKeys.notifications, filter],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      api.getNotifications({ limit: 20, cursor: pageParam, filter }),
    getNextPageParam: (lastPage: Notification[]) =>
      lastPage.length >= 20 ? lastPage[lastPage.length - 1]?.created_at : undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!user,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  });
}

export function useUnreadNotificationCount() {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.unreadNotificationCount,
    queryFn: () => api.getUnreadNotificationCount(),
    enabled: !!user,
    initialData: 0,
    staleTime: 0,
    gcTime: 60 * 1000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
      queryClient.invalidateQueries({ queryKey: queryKeys.unreadNotificationCount });
      queryClient.invalidateQueries({ queryKey: queryKeys.unreadFeedCount });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
      queryClient.invalidateQueries({ queryKey: queryKeys.unreadNotificationCount });
      queryClient.invalidateQueries({ queryKey: queryKeys.unreadFeedCount });
    },
  });
}

export function useUnreadFeedCount() {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.unreadFeedCount,
    queryFn: () => api.getUnreadFeedCount(),
    enabled: !!user,
    initialData: 0,
    staleTime: 0,
    gcTime: 60 * 1000,
  });
}

export function useMarkPostNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (postId: string) => api.markNotificationsReadForPost(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
      queryClient.invalidateQueries({ queryKey: queryKeys.unreadNotificationCount });
      queryClient.invalidateQueries({ queryKey: queryKeys.unreadFeedCount });
    },
  });
}

export function useArchiveNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.archiveNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
      queryClient.invalidateQueries({ queryKey: queryKeys.unreadNotificationCount });
      queryClient.invalidateQueries({ queryKey: queryKeys.unreadFeedCount });
    },
  });
}

export function useSnoozeNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, until }: { id: string; until: string }) => api.snoozeNotification(id, until),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
      queryClient.invalidateQueries({ queryKey: queryKeys.unreadNotificationCount });
      queryClient.invalidateQueries({ queryKey: queryKeys.unreadFeedCount });
    },
  });
}

// ===========================================
// Notification Preferences Hooks
// ===========================================

export function useNotificationPreferences() {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: ['notificationPreferences'],
    queryFn: () => api.getNotificationPreferences(),
    enabled: !!user,
    staleTime: 60_000,
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (prefs: Parameters<typeof api.upsertNotificationPreferences>[0]) =>
      api.upsertNotificationPreferences(prefs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationPreferences'] });
    },
  });
}

// ===========================================
// Contact Hooks
// ===========================================

export function useContacts(filters?: { type?: Contact['type']; status?: Contact['status']; search?: string }) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.contacts(filters),
    queryFn: () => api.getContacts(filters),
    enabled: !!user,
    initialData: [],
  });
}

export function useContact(id: string) {
  return useQuery({
    queryKey: queryKeys.contact(id),
    queryFn: () => api.getContact(id),
    enabled: !!id,
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contact: Omit<Contact, 'id' | 'user_id' | 'created_at' | 'updated_at'>) =>
      api.createContact(contact),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Contact> }) =>
      api.updateContact(id, updates),
    onSuccess: (updatedContact) => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.setQueryData(queryKeys.contact(updatedContact.id), updatedContact);
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

// ===========================================
// Deal Hooks
// ===========================================

export function useDeals(filters?: { stage?: Deal['stage']; contact_id?: string }) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.deals(filters),
    queryFn: () => api.getDeals(filters),
    enabled: !!user,
    initialData: [],
  });
}

export function useDeal(id: string) {
  return useQuery({
    queryKey: queryKeys.deal(id),
    queryFn: () => api.getDeal(id),
    enabled: !!id,
  });
}

export function useCreateDeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deal: Omit<Deal, 'id' | 'user_id' | 'created_at' | 'updated_at'>) =>
      api.createDeal(deal),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useUpdateDeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Deal> }) =>
      api.updateDeal(id, updates),
    onSuccess: (updatedDeal) => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.setQueryData(queryKeys.deal(updatedDeal.id), updatedDeal);
    },
  });
}

export function useDeleteDeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteDeal(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

// ===========================================
// Project Hooks
// ===========================================

export function useProjects(filters?: { status?: Project['status'] }) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.projects(filters),
    queryFn: () => api.getProjects(filters),
    enabled: !!user,
    initialData: [],
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: queryKeys.project(id),
    queryFn: () => api.getProject(id),
    enabled: !!id,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (project: Omit<Project, 'id' | 'user_id' | 'created_at' | 'updated_at'>) =>
      api.createProject(project),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Project> }) =>
      api.updateProject(id, updates),
    onSuccess: (updatedProject) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.setQueryData(queryKeys.project(updatedProject.id), updatedProject);
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

// ===========================================
// Financial Hooks
// ===========================================

export function useFinancialMetrics(limit = 12) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.financialMetrics,
    queryFn: () => api.getFinancialMetrics(limit),
    enabled: !!user,
    initialData: [],
  });
}

export function useLatestFinancialMetrics() {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.latestFinancialMetrics,
    queryFn: () => api.getLatestFinancialMetrics(),
    enabled: !!user,
  });
}

export function useSaveFinancialMetrics() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (metrics: Parameters<typeof api.saveFinancialMetrics>[0]) =>
      api.saveFinancialMetrics(metrics),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.financialMetrics });
      queryClient.invalidateQueries({ queryKey: queryKeys.latestFinancialMetrics });
    },
  });
}

export function useAssets(type?: Asset['type']) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.assets(type),
    queryFn: () => api.getAssets(type),
    enabled: !!user,
    initialData: [],
  });
}

export function useCreateAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (asset: Omit<Asset, 'id' | 'user_id' | 'created_at' | 'updated_at'>) =>
      api.createAsset(asset),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useUpdateAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Asset> }) =>
      api.updateAsset(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useDeleteAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteAsset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useInvoices(filters?: { status?: Invoice['status']; contact_id?: string }) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.invoices(filters),
    queryFn: () => api.getInvoices(filters),
    enabled: !!user,
    initialData: [],
  });
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invoice: Omit<Invoice, 'id' | 'user_id' | 'created_at' | 'updated_at'>) =>
      api.createInvoice(invoice),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Invoice> }) =>
      api.updateInvoice(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

// ===========================================
// Library Assets Hooks (ClickUp-synced business assets)
// ===========================================

export function useLibraryAssets(filters?: LibraryAssetFilters) {
  return useQuery({
    queryKey: queryKeys.libraryAssets(filters),
    queryFn: () => api.getLibraryAssets(filters),
    // Always enabled - the API client handles auth and returns empty if no session
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: keepPreviousData, // Keep previous data visible during refetch
  });
}

// ===========================================
// Alfie (AI) Hooks
// ===========================================

/**
 * Stable identity, so the placeholder below is the same array on every render.
 */
const NO_THREADS: AlfieThread[] = [];

export function useAlfieThreads(limit = 20) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.alfieThreads,
    queryFn: () => api.getAlfieThreads(limit),
    enabled: !!user,
    // `placeholderData`, NOT `initialData`: initial data is written into the cache and stamped
    // fresh at mount, and the client's default `staleTime` is 5 minutes (main.tsx), so the
    // query never fetched on mount. Home reads this list only for "have you asked Alfie
    // anything", and the empty array left that row open for builders with a chat history.
    // A placeholder is never cached, so the fetch still happens and the answer replaces it.
    placeholderData: NO_THREADS,
  });
}

export function useCreateAlfieThread() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ title, contextType, contextId }: { title: string; contextType?: AlfieThread['context_type']; contextId?: string }) =>
      api.createAlfieThread(title, contextType, contextId),
    onSuccess: (newThread) => {
      queryClient.setQueryData<AlfieThread[]>(queryKeys.alfieThreads, (old = []) => [
        newThread,
        ...old,
      ]);
    },
  });
}

export function useArchiveAlfieThread() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.archiveAlfieThread(id),
    onSuccess: (_, archivedId) => {
      queryClient.setQueryData<AlfieThread[]>(queryKeys.alfieThreads, (old = []) =>
        old.filter((t) => t.id !== archivedId)
      );
    },
  });
}

export function useAlfieMessages(threadId: string, limit = 50) {
  return useQuery({
    queryKey: queryKeys.alfieMessages(threadId),
    queryFn: () => api.getAlfieMessages(threadId, limit),
    enabled: !!threadId,
    initialData: [],
  });
}

export function useSendAlfieMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ threadId, content, attachments }: { threadId: string; content: string; attachments?: MessageAttachment[] }) =>
      api.sendAlfieMessage(threadId, content, attachments),
    onSuccess: (newMessage, { threadId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.alfieMessages(threadId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.alfieThreads });
    },
  });
}

export function useAlfieInsights(limit = 10) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.alfieInsights,
    queryFn: () => api.getAlfieInsights(limit),
    enabled: !!user,
    initialData: [],
  });
}

export function useUpdateAlfieInsightStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: AlfieInsight['status'] }) =>
      api.updateAlfieInsightStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.alfieInsights });
    },
  });
}

// ===========================================
// Learning Hooks (LMS)
// ===========================================

export function useLearningPaths(category?: string) {
  return useQuery({
    queryKey: queryKeys.learningPaths(category),
    queryFn: () => api.getLearningPaths(category),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useLearningPath(id: string) {
  return useQuery({
    queryKey: queryKeys.learningPath(id),
    queryFn: () => api.getLearningPath(id),
    enabled: !!id,
  });
}

export function useCourses(category?: string) {
  return useQuery({
    queryKey: queryKeys.courses(category),
    queryFn: () => api.getCourses(category),
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Keep the previous courses (and thus the filter bar + grid) on screen while a
    // new pillar loads — otherwise the whole block flips to `isLoading` and vanishes.
    placeholderData: keepPreviousData,
  });
}

export function useCourseLinkedAssets(courseId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['course-linked-assets', courseId],
    queryFn: () => api.getCourseLinkedAssets(courseId),
    enabled: enabled && !!courseId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCourse(id: string) {
  return useQuery({
    queryKey: queryKeys.course(id),
    queryFn: () => api.getCourse(id),
    enabled: !!id,
  });
}

/**
 * The course's running order, for surfaces that need to name a lesson without opening the
 * course. Cached apart from `useCourse`, which fetches the same lessons with every join the
 * course page needs.
 */
export function useCourseLessonOutline(courseId: string) {
  return useQuery({
    queryKey: ['courseLessonOutline', courseId],
    queryFn: () => api.getCourseLessonOutline(courseId),
    enabled: !!courseId,
    staleTime: 5 * 60 * 1000, // 5 minutes — a course's lesson list barely moves
  });
}

export function useLesson(id: string) {
  return useQuery({
    queryKey: queryKeys.lesson(id),
    queryFn: () => api.getLesson(id),
    enabled: !!id,
    throwOnError: false, // surface errors via `error` state, not error boundary crash
    retry: false,
  });
}

// ---- Enrollments ----
export function useMyEnrollments() {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.myEnrollments,
    queryFn: () => api.getMyEnrollments(),
    enabled: !!user,
  });
}

export function useEnrollInPath() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pathId: string) => api.enrollInPath(pathId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myEnrollments });
    },
  });
}

export function useUpdatePathEnrollment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pathId, updates }: { pathId: string; updates: Partial<PathEnrollment> }) =>
      api.updatePathEnrollment(pathId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myEnrollments });
    },
  });
}

// ---- Assessments ----
export function useAssessment(id: string) {
  return useQuery({
    queryKey: queryKeys.assessment(id),
    queryFn: () => api.getAssessment(id),
    enabled: !!id,
  });
}

export function useModuleAssessments(moduleId: string) {
  return useQuery({
    queryKey: queryKeys.moduleAssessments(moduleId),
    queryFn: () => api.getModuleAssessments(moduleId),
    enabled: !!moduleId,
  });
}

export function useStartAssessmentAttempt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assessmentId: string) => api.startAssessmentAttempt(assessmentId),
    onSuccess: (_, assessmentId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myAssessmentAttempts(assessmentId) });
    },
  });
}

export function useSubmitAssessmentAttempt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ attemptId, answers }: { attemptId: string; answers: Record<string, unknown> }) =>
      api.submitAssessmentAttempt(attemptId, answers),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myAssessmentAttempts(result.assessment_id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.myEnrollments });
      // Refresh best score and enrollment status (course completion may have changed)
      queryClient.invalidateQueries({ queryKey: ['bestAssessmentScore'] });
      queryClient.invalidateQueries({ queryKey: ['courseEnrollment'] });
    },
  });
}

export function useMyAssessmentAttempts(assessmentId?: string) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.myAssessmentAttempts(assessmentId),
    queryFn: () => api.getMyAssessmentAttempts(assessmentId),
    enabled: !!user,
  });
}

export function useBestAssessmentScore(courseId: string) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.bestAssessmentScore(courseId),
    queryFn: () => api.getBestAssessmentScore(courseId),
    enabled: !!user && !!courseId,
  });
}

// ---- Certificates ----
export function useMyCertificates() {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.myCertificates,
    queryFn: () => api.getMyCertificates(),
    enabled: !!user,
  });
}

// ---- Badges (Gamification) ----
export function useAllBadges() {
  return useQuery({
    queryKey: queryKeys.allBadges,
    queryFn: () => api.getAllBadges(),
    staleTime: 10 * 60 * 1000, // 10 minutes - badges don't change often
  });
}

export function useMyBadges() {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.myBadges,
    queryFn: () => api.getMyBadges(),
    enabled: !!user,
  });
}

export function useBadgesWithEarnedStatus() {
  return useQuery({
    queryKey: queryKeys.badgesWithEarnedStatus,
    queryFn: () => api.getBadgesWithEarnedStatus(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ---- Points ----
export function useMyPoints() {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.myPoints,
    queryFn: () => api.getMyPoints(),
    enabled: !!user,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// ---- Leaderboard ----
export function useLeaderboard(timeframe: 'weekly' | 'monthly' | 'all_time' = 'all_time') {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.leaderboard(timeframe),
    queryFn: () => api.getLeaderboard(timeframe),
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ---- Challenges ----
export function useChallenges() {
  return useQuery({
    queryKey: queryKeys.challenges,
    queryFn: () => api.getChallenges(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useMyChallenges() {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.myChallenges,
    queryFn: () => api.getMyChallenges(),
    enabled: !!user,
  });
}

export function useJoinChallenge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (challengeId: string) => api.joinChallenge(challengeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.challenges });
      queryClient.invalidateQueries({ queryKey: queryKeys.myChallenges });
    },
  });
}

export function useEvaluateBadges() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.triggerBadgeEvaluation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.badgesWithEarnedStatus });
      queryClient.invalidateQueries({ queryKey: queryKeys.myBadges });
      queryClient.invalidateQueries({ queryKey: queryKeys.myPoints });
      // Invalidate all leaderboard timeframes
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
    },
  });
}

// ---- Legacy Progress Hooks (for backward compat with Learn page) ----
export function useUserLearningProgress(learningPathId?: string) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.userLearningProgress(learningPathId),
    queryFn: () => api.getUserLearningProgress(learningPathId),
    enabled: !!user,
    initialData: [],
  });
}

export function useUpdateLearningProgress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (progress: Omit<UserLearningProgress, 'id' | 'user_id' | 'created_at' | 'updated_at'>) =>
      api.updateLearningProgress(progress),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userLearningProgress(updated.learning_path_id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.myEnrollments });
    },
  });
}

// ===========================================
// Author CRUD Hooks (Content Management)
// ===========================================

// ---- Learning Path CRUD ----
export function useCreateLearningPath() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof api.createLearningPath>[0]) =>
      api.createLearningPath(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['learningPaths'] });
    },
  });
}

export function useUpdateLearningPath() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Parameters<typeof api.updateLearningPath>[1] }) =>
      api.updateLearningPath(id, updates),
    onSuccess: (updatedPath) => {
      queryClient.invalidateQueries({ queryKey: ['learningPaths'] });
      queryClient.setQueryData(queryKeys.learningPath(updatedPath.id), updatedPath);
    },
  });
}

export function useDeleteLearningPath() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteLearningPath(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['learningPaths'] });
    },
  });
}

export function usePublishLearningPath() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.publishLearningPath(id),
    onSuccess: (updatedPath) => {
      queryClient.invalidateQueries({ queryKey: ['learningPaths'] });
      queryClient.setQueryData(queryKeys.learningPath(updatedPath.id), updatedPath);
    },
  });
}

// ---- Course CRUD ----
export function useCreateCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof api.createCourse>[0]) =>
      api.createCourse(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['learningPaths'] });
    },
  });
}

export function useUpdateCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Parameters<typeof api.updateCourse>[1] }) =>
      api.updateCourse(id, updates),
    onSuccess: (updatedCourse) => {
      queryClient.invalidateQueries({ queryKey: ['learningPaths'] });
      queryClient.setQueryData(queryKeys.course(updatedCourse.id), updatedCourse);
    },
  });
}

export function useDeleteCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteCourse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['learningPaths'] });
    },
  });
}

// ---- Module CRUD ----
export function useCreateModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof api.createModule>[0]) =>
      api.createModule(data),
    onSuccess: (newModule) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.course(newModule.course_id) });
    },
  });
}

export function useUpdateModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Parameters<typeof api.updateModule>[1] }) =>
      api.updateModule(id, updates),
    onSuccess: (updatedModule) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.course(updatedModule.course_id) });
    },
  });
}

export function useDeleteModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ moduleId, courseId }: { moduleId: string; courseId: string }) =>
      api.deleteModule(moduleId),
    onSuccess: (_, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.course(courseId) });
    },
  });
}

export function useReorderModules() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ courseId, moduleIds }: { courseId: string; moduleIds: string[] }) =>
      api.reorderModules(courseId, moduleIds),
    onSuccess: (_, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.course(courseId) });
    },
  });
}

// ---- Lesson CRUD ----
export function useCreateLesson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof api.createLesson>[0]) =>
      api.createLesson(data),
    onSuccess: (newLesson) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.course(newLesson.course_id) });
    },
  });
}

export function useUpdateLesson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Parameters<typeof api.updateLesson>[1] }) =>
      api.updateLesson(id, updates),
    onSuccess: (updatedLesson) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.course(updatedLesson.course_id) });
      queryClient.setQueryData(queryKeys.lesson(updatedLesson.id), updatedLesson);
    },
  });
}

export function useDeleteLesson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lessonId, courseId }: { lessonId: string; courseId: string }) =>
      api.deleteLesson(lessonId),
    onSuccess: (_, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.course(courseId) });
    },
  });
}

export function useReorderLessons() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ moduleId, lessonIds, courseId }: { moduleId: string; lessonIds: string[]; courseId: string }) =>
      api.reorderLessons(moduleId, lessonIds),
    onSuccess: (_, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.course(courseId) });
    },
  });
}

// ---- Path-Course Association ----
export function useAddCourseToPath() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pathId, courseId, order, isRequired }: {
      pathId: string;
      courseId: string;
      order: number;
      isRequired?: boolean;
    }) => api.addCourseToPath(pathId, courseId, order, isRequired),
    onSuccess: (_, { pathId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.learningPath(pathId) });
    },
  });
}

export function useRemoveCourseFromPath() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pathId, courseId }: { pathId: string; courseId: string }) =>
      api.removeCourseFromPath(pathId, courseId),
    onSuccess: (_, { pathId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.learningPath(pathId) });
    },
  });
}

export function useReorderPathCourses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pathId, courseIds }: { pathId: string; courseIds: string[] }) =>
      api.reorderPathCourses(pathId, courseIds),
    onSuccess: (_, { pathId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.learningPath(pathId) });
    },
  });
}

// ---- Assessment CRUD ----
export function useCreateAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof api.createAssessment>[0]) =>
      api.createAssessment(data),
    onSuccess: (newAssessment) => {
      if (newAssessment.module_id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.moduleAssessments(newAssessment.module_id) });
      }
    },
  });
}

export function useUpdateAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Parameters<typeof api.updateAssessment>[1] }) =>
      api.updateAssessment(id, updates),
    onSuccess: (updatedAssessment) => {
      queryClient.setQueryData(queryKeys.assessment(updatedAssessment.id), updatedAssessment);
      if (updatedAssessment.module_id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.moduleAssessments(updatedAssessment.module_id) });
      }
    },
  });
}

export function useDeleteAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ assessmentId, moduleId }: { assessmentId: string; moduleId?: string }) =>
      api.deleteAssessment(assessmentId),
    onSuccess: (_, { moduleId }) => {
      if (moduleId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.moduleAssessments(moduleId) });
      }
    },
  });
}

// ---- Question CRUD ----
export function useCreateQuestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof api.createQuestion>[0]) =>
      api.createQuestion(data),
    onSuccess: (newQuestion) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assessment(newQuestion.assessment_id) });
    },
  });
}

export function useUpdateQuestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Parameters<typeof api.updateQuestion>[1] }) =>
      api.updateQuestion(id, updates),
    onSuccess: (updatedQuestion) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assessment(updatedQuestion.assessment_id) });
    },
  });
}

export function useDeleteQuestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ questionId, assessmentId }: { questionId: string; assessmentId: string }) =>
      api.deleteQuestion(questionId),
    onSuccess: (_, { assessmentId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assessment(assessmentId) });
    },
  });
}

export function useReorderQuestions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ assessmentId, questionIds }: { assessmentId: string; questionIds: string[] }) =>
      api.reorderQuestions(assessmentId, questionIds),
    onSuccess: (_, { assessmentId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assessment(assessmentId) });
    },
  });
}

// ===========================================
// Progress Tracking Hooks
// ===========================================

export function useMarkLessonComplete() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lessonId, courseId }: { lessonId: string; courseId: string; lessonName?: string }) =>
      api.markLessonComplete(lessonId, courseId),
    onMutate: async () => {
      // Optimistic update: immediately add 10 points to the displayed total
      await queryClient.cancelQueries({ queryKey: queryKeys.myPoints });
      const previous = queryClient.getQueryData<{ total: number; transactions: unknown[] }>(queryKeys.myPoints);
      if (previous) {
        queryClient.setQueryData(queryKeys.myPoints, {
          ...previous,
          total: previous.total + 10,
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      // Roll back optimistic update on failure
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.myPoints, context.previous);
      }
    },
    onSuccess: (_, { courseId, lessonId, lessonName }) => {
      queryClient.invalidateQueries({ queryKey: ['lessonProgress', courseId] });
      queryClient.invalidateQueries({ queryKey: ['courseEnrollment', courseId] });
      queryClient.invalidateQueries({ queryKey: ['myCourseEnrollments'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.myEnrollments });
      // Award 10 gamification points + evaluate badges (fire-and-forget, non-blocking)
      api.awardLessonPoints(lessonId, lessonName || 'Lesson').then(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.myPoints });
      }).catch(() => {});
      api.triggerBadgeEvaluation().then(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.badgesWithEarnedStatus });
        queryClient.invalidateQueries({ queryKey: queryKeys.myBadges });
        queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
      }).catch(() => {});
    },
  });
}

export function useMarkLessonStarted() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lessonId, courseId }: { lessonId: string; courseId: string }) =>
      api.markLessonStarted(lessonId, courseId),
    onSuccess: (_, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: ['lessonProgress', courseId] });
    },
    throwOnError: false, // auto-start is non-critical, never crash the page
  });
}

export function useUpdateLessonProgress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lessonId, courseId, progress }: {
      lessonId: string;
      courseId: string;
      progress: { progress_percentage?: number; time_spent_seconds?: number };
    }) => api.updateLessonProgress(lessonId, progress),
    onSuccess: (_, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: ['lessonProgress', courseId] });
    },
  });
}

export function useLessonProgress(courseId: string) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: ['lessonProgress', courseId],
    queryFn: () => api.getLessonProgress(courseId),
    enabled: !!user && !!courseId,
    staleTime: 60 * 1000, // 1 minute — progress changes on user actions; mutations already invalidate
  });
}

export function useEnrollInCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (courseId: string) => api.enrollInCourse(courseId),
    onSuccess: (_, courseId) => {
      queryClient.invalidateQueries({ queryKey: ['courseEnrollment', courseId] });
      queryClient.invalidateQueries({ queryKey: ['myCourseEnrollments'] });
    },
  });
}

export function useCourseEnrollment(courseId: string) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: ['courseEnrollment', courseId],
    queryFn: () => api.getCourseEnrollment(courseId),
    enabled: !!user && !!courseId,
  });
}

export function useMyCourseEnrollments() {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: ['myCourseEnrollments'],
    queryFn: () => api.getMyCourseEnrollments(),
    enabled: !!user,
  });
}

export function useUpdatePathProgress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pathId: string) => api.updatePathProgress(pathId),
    onSuccess: (_, pathId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.learningPath(pathId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.myEnrollments });
    },
  });
}

// ===========================================
// Coaching & Guidance Hooks
// ===========================================

export function useCoaches(specialty?: string) {
  return useQuery({
    queryKey: queryKeys.coaches(specialty),
    queryFn: () => api.getCoaches(specialty),
    initialData: [],
  });
}

export function useCoach(id: string) {
  return useQuery({
    queryKey: queryKeys.coach(id),
    queryFn: () => api.getCoach(id),
    enabled: !!id,
  });
}

export function useGuidanceCalls(filters?: { status?: GuidanceCall['status']; upcoming?: boolean }) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.guidanceCalls(filters),
    queryFn: () => api.getGuidanceCalls(filters),
    enabled: !!user,
    initialData: [],
  });
}

export function useUpcomingCalls(limit = 5) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.upcomingCalls,
    queryFn: () => api.getUpcomingCalls(limit),
    enabled: !!user,
    initialData: [],
  });
}

export function useBookGuidanceCall() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (call: Omit<GuidanceCall, 'id' | 'user_id' | 'status' | 'created_at' | 'updated_at'>) =>
      api.bookGuidanceCall(call),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guidanceCalls'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.upcomingCalls });
    },
  });
}

export function useUpdateGuidanceCall() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<GuidanceCall> }) =>
      api.updateGuidanceCall(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guidanceCalls'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.upcomingCalls });
    },
  });
}

export function useCancelGuidanceCall() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.cancelGuidanceCall(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guidanceCalls'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.upcomingCalls });
    },
  });
}

// ===========================================
// Activity Hooks
// ===========================================

export function useRecentActivity(options?: {
  entityType?: string;
  sinceHours?: number;
  limit?: number;
}) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.recentActivity(options),
    queryFn: () => api.getRecentActivity(options),
    enabled: !!user,
    initialData: [],
    staleTime: 5 * 60 * 1000,      // 5 minutes
    gcTime: 24 * 60 * 60 * 1000,   // 24 hours (offline)
  });
}

export function useLogActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activity: Omit<Activity, 'id' | 'user_id' | 'created_at'>) =>
      api.logActivity(activity),
    onSuccess: () => {
      // Invalidate all recentActivity queries (with any options)
      queryClient.invalidateQueries({ queryKey: ['recentActivity'] });
    },
  });
}

export function useActionPlanSessions(filters?: { status?: ActionPlanSession['status']; upcoming?: boolean }) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.actionPlanSessions(filters),
    queryFn: () => api.getActionPlanSessions(filters),
    enabled: !!user,
    initialData: [],
  });
}

export function useUpcomingSessions(limit = 5) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.upcomingSessions,
    queryFn: () => api.getUpcomingSessions(limit),
    enabled: !!user,
    initialData: [],
  });
}

// ===========================================
// Media Upload Hooks
// ===========================================

export function useMediaAsset(id: string | null) {
  return useQuery({
    queryKey: queryKeys.mediaAsset(id!),
    queryFn: () => api.getMediaAsset(id!),
    enabled: !!id,
  });
}

export function useMediaAssets(filters?: {
  purpose?: MediaAssetPurpose;
  status?: MediaAssetStatus;
  assetType?: MediaAssetType;
}) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.mediaAssets(filters),
    queryFn: () => api.getMediaAssets(filters),
    enabled: !!user,
    initialData: [],
  });
}

export function useInitMediaUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      filename,
      size,
      mimeType,
      purpose,
      organizationId,
    }: {
      filename: string;
      size: number;
      mimeType: string;
      purpose?: MediaAssetPurpose;
      organizationId?: string;
    }) => api.initMediaUpload(filename, size, mimeType, purpose, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mediaAssets'] });
    },
  });
}

export function useCompleteMediaUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assetId: string) => api.completeMediaUpload(assetId),
    onSuccess: (_, assetId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.mediaAsset(assetId) });
      queryClient.invalidateQueries({ queryKey: ['mediaAssets'] });
    },
  });
}

export function useUploadFileToStorage() {
  return useMutation({
    mutationFn: ({
      uploadUrl,
      file,
      onProgress,
    }: {
      uploadUrl: string;
      file: File;
      onProgress?: (percent: number) => void;
    }) => api.uploadFileToStorage(uploadUrl, file, onProgress),
  });
}

// ===========================================
// Media Prefetch Hooks
// ===========================================
//
// Prime the browser's HTTP cache so opening a lesson feels instant. All three
// hooks funnel into the module-level singleton `mediaPrefetchCoordinator` —
// dedup, byte budget, concurrency, and abort behaviour live there.
//
// Lessons without `playback_url` (still transcoding) are silently skipped —
// the URL builder returns an empty array and the coordinator no-ops.
//
// So are lessons served by Cloudflare Stream (ticket 86d40p593). Their address
// is a short-lived signed pass the player renews for itself, so there is no
// stable URL to warm, and the prefetcher's cache-buster would corrupt the token
// in the path. `stream_status` has to be carried this far for the URL builders
// to see it — the guard lives in `isPrefetchable`, not here.

interface LessonWithVideoMedia {
  id: string;
  video_asset_id?: string | null;
  video_media?: {
    playback_url?: string | null;
    poster_url?: string | null;
    stream_status?: string | null;
    metadata?: Record<string, unknown> | null;
  } | null;
}

/** Extract the asset shape `tier1UrlsForAsset` / `tier2UrlsForAsset` expects. */
function lessonToAsset(lesson: LessonWithVideoMedia) {
  return lesson.video_media
    ? {
        playback_url: lesson.video_media.playback_url ?? null,
        poster_url: lesson.video_media.poster_url ?? null,
        stream_status: lesson.video_media.stream_status ?? null,
      }
    : null;
}

/**
 * Prime tier 1 (manifest + variant playlist + poster) for every lesson in a
 * course that has finished transcoding. Fires once on mount; aborts on
 * unmount. Re-runs when the course's lesson list changes (e.g. new lesson
 * goes ready).
 */
export function useCoursePrefetch(courseId: string | null | undefined) {
  const { data: course } = useCourse(courseId ?? '');

  // Stable list of lesson asset shapes — only changes when underlying URLs do.
  const lessons = useMemo<LessonWithVideoMedia[]>(() => {
    const rawLessons = (course as { lessons?: LessonWithVideoMedia[] } | undefined)?.lessons;
    return Array.isArray(rawLessons) ? rawLessons : [];
  }, [course]);

  // Key derived from playback URLs only — re-renders that don't change URLs
  // don't trigger a new effect, so coordinator dedup never kicks in.
  const playbackKey = useMemo(
    () => lessons.map((l) => l.video_media?.playback_url ?? '').join('|'),
    [lessons],
  );

  useEffect(() => {
    if (!mediaPrefetchCoordinator || !courseId) return;
    const controller = new AbortController();
    for (const lesson of lessons) {
      const asset = lessonToAsset(lesson);
      const urls = tier1UrlsForAsset(asset);
      mediaPrefetchCoordinator.enqueueAll(
        urls,
        PREFETCH_PRIORITY.currentCourseTier1,
        ESTIMATE_BYTES.manifest + ESTIMATE_BYTES.variantPlaylist + ESTIMATE_BYTES.poster,
        controller.signal,
      );
    }
    return () => controller.abort();
    // playbackKey captures URL identity; lessons reference captured for actual data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, playbackKey]);
}

/**
 * Prime tier 2 (first 480p segment) for the next 1–2 lessons in sequence
 * after `currentLessonId`. Aborts on lesson change.
 */
export function useNextLessonPrefetch(
  courseId: string | null | undefined,
  currentLessonId: string | null | undefined,
) {
  const { data: course } = useCourse(courseId ?? '');

  const lessons = useMemo<LessonWithVideoMedia[]>(() => {
    const rawLessons = (course as { lessons?: LessonWithVideoMedia[] } | undefined)?.lessons;
    return Array.isArray(rawLessons) ? rawLessons : [];
  }, [course]);

  // Snapshot the next 1–2 lesson asset shapes deterministically.
  const upcoming = useMemo(() => {
    if (!currentLessonId) return [] as LessonWithVideoMedia[];
    const idx = lessons.findIndex((l) => l.id === currentLessonId);
    if (idx < 0) return [];
    return lessons.slice(idx + 1, idx + 3);
  }, [lessons, currentLessonId]);

  const upcomingKey = useMemo(
    () =>
      upcoming.map((l) => `${l.id}:${l.video_media?.playback_url ?? ''}`).join('|'),
    [upcoming],
  );

  useEffect(() => {
    if (!mediaPrefetchCoordinator || !courseId || !currentLessonId) return;
    const controller = new AbortController();
    for (const lesson of upcoming) {
      const asset = lessonToAsset(lesson);
      // Tier 1 first (cheap) so manifest is warm, then tier 2 (segment).
      mediaPrefetchCoordinator.enqueueAll(
        tier1UrlsForAsset(asset),
        PREFETCH_PRIORITY.nextLessonTier2,
        ESTIMATE_BYTES.manifest + ESTIMATE_BYTES.variantPlaylist + ESTIMATE_BYTES.poster,
        controller.signal,
      );
      mediaPrefetchCoordinator.enqueueAll(
        tier2UrlsForAsset(asset),
        PREFETCH_PRIORITY.nextLessonTier2,
        ESTIMATE_BYTES.firstSegment,
        controller.signal,
      );
    }
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, currentLessonId, upcomingKey]);
}

/**
 * Lowest priority: prime thumbnails + lesson posters for the user's OTHER
 * in-progress courses (status='in_progress', excluding the one they're
 * currently in). Limited to the 3 most recently accessed.
 *
 * Uses only data already in the enrollment payload — no extra round trips.
 * Doesn't fetch manifests for these (those will be primed when the user
 * navigates into the course via `useCoursePrefetch`).
 */
export function useInProgressCoursesPrefetch(currentCourseId: string | null | undefined) {
  const { data: enrollments } = useMyCourseEnrollments();

  // Snapshot just the URLs we plan to prefetch, sorted by recency from the API.
  const urls = useMemo<string[]>(() => {
    if (!Array.isArray(enrollments)) return [];
    const out: string[] = [];
    let count = 0;
    for (const enrollment of enrollments) {
      const e = enrollment as {
        status?: string;
        course?: {
          id?: string;
          thumbnail_media?: { poster_url?: string | null } | null;
          lesson_posters?: Array<{
            video_media?: { poster_url?: string | null } | null;
          }> | null;
        };
      };
      if (e.status !== 'in_progress') continue;
      if (!e.course?.id || e.course.id === currentCourseId) continue;
      if (count >= 3) break;
      count++;

      const thumb = resolvePrefetchUrl(e.course.thumbnail_media?.poster_url);
      if (thumb) out.push(thumb);
      for (const lp of e.course.lesson_posters ?? []) {
        const p = resolvePrefetchUrl(lp.video_media?.poster_url);
        if (p) {
          out.push(p);
          // 1 poster per course is enough — the course landing only shows one
          // hero image. Avoid bloating the budget on poster sprites.
          break;
        }
      }
    }
    return out;
  }, [enrollments, currentCourseId]);

  const urlsKey = useMemo(() => urls.join('|'), [urls]);

  useEffect(() => {
    if (!mediaPrefetchCoordinator || urls.length === 0) return;
    const controller = new AbortController();
    mediaPrefetchCoordinator.enqueueAll(
      urls,
      PREFETCH_PRIORITY.otherInProgressCourse,
      ESTIMATE_BYTES.poster,
      controller.signal,
    );
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlsKey]);
}

// ===========================================
// Voice Settings Hooks
// ===========================================

export function useVoiceSettings() {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: queryKeys.voiceSettings(user?.id),
    queryFn: () => api.getUserVoiceSettings(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 24 * 60 * 60 * 1000, // 24 hours (offline support)
  });
}

export function useUpdateVoiceSettings() {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();

  return useMutation({
    mutationFn: (settings: Partial<NewUserVoiceSettings>) =>
      api.updateUserVoiceSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.voiceSettings(user?.id) });
    },
  });
}

// ===========================================
// Access Matrix Hooks
// ===========================================

export function useAccessMatrix() {
  return useQuery({
    queryKey: queryKeys.accessMatrix,
    queryFn: () => api.getAccessMatrix(),
    staleTime: 5 * 60 * 1000,
  });
}

// ===========================================
// Community Messaging Hooks (re-exported)
// ===========================================

export {
  communityKeys,
  useConversations,
  useConversation,
  useMessages,
  useUnreadCounts,
  useSendMessage,
  useEditMessage,
  useDeleteMessage,
  useAddReaction,
  useRemoveReaction,
  useToggleReaction,
  useMarkAsRead,
  useCreateDM,
  useTierConversation,
  useTogglePin,
  useMuteConversation,
  useArchiveConversation,
  // Phase 6 — Directory & Leads
  useDirectoryMembers,
  useDirectoryExperts,
  useDirectoryMapPins,
  useDirectoryMember,
  useFindOrCreateDm,
  useLeads,
  useCreateLead,
  useUpdateLeadStatus,
  // Phase 7 — Attachments & Search
  useSignedUrl,
  useSearchMessages,
  // Community Engagement / Gamification
  useCommunityLeaderboard,
  useMyCommunityStats,
  useCommunityBadges,
  useCommunityPerks,
  usePurchaseCommunityPerk,
} from './community-hooks';

export {
  useConversationRealtime,
  reconcileConversationMessages,
  useUserChannel,
  useNotificationRealtime,
  usePresence,
  useTyping,
  formatTypingText,
  type UserPresence,
  type UseTypingReturn,
} from './community-realtime';

export { REALTIME_EVENTS } from './realtime-events';
